/** Which job the logger core is doing right now (PE-22 slice A).
 *
 *  Before this contract the distinction lived as a boolean named
 *  `isPastSessionEdit`, re-derived independently in six places. It carried
 *  TWO concerns at once, and prescription needs one of them without the
 *  other:
 *
 *  - the CLOCK — where a completion timestamp comes from
 *  - the LIVE CUES: rest countdown, bodyweight marker, editable controls
 *
 *  | mode        | clock                    | live cues |
 *  |-------------|--------------------------|-----------|
 *  | 'log'       | the wall clock, now      | on        |
 *  | 'log-past'  | placed inside the window | off       |
 *  | 'prescribe' | none — nothing happened  | off       |
 *
 *  So the two questions are asked separately: the clock asks
 *  `mode === 'log-past'`, the live cues ask `mode !== 'log'`. Collapsing
 *  them back into one boolean is what made a prescribed set look performed.
 *
 *  `'prescribe'` has NO producer yet — `deriveLoggerMode` never returns it.
 *  It arrives with slices C/D, which is why today's behaviour is unchanged
 *  bit for bit. */
export type LoggerMode = 'log' | 'log-past' | 'prescribe';

/** The single derivation of the mode from the editing target.
 *
 *  `editing.mode` is written explicitly only for 'repeat'; a plain
 *  edit-from-history creates the target WITHOUT the field (the read-time
 *  default fills it as 'edit'), so a missing mode means edit.
 *
 *  A REPEAT is live logging: it clones the structure into a brand-new
 *  session with a fresh `startedAt`, so the athlete is training now. Only a
 *  true edit is correcting history. */
export const deriveLoggerMode = (
  /** Structural param, not the LoggerScreen `EditingTarget` import: an
   *  EditingTarget satisfies it, and the predicate stays pure and unit-
   *  testable without a cast in the double. Same reasoning that already
   *  governed shouldPersistDraft. */
  editing: { mode?: 'edit' | 'repeat' } | null,
): LoggerMode => (editing != null && editing.mode !== 'repeat' ? 'log-past' : 'log');

/** The identity of the session on screen (#418): the key of the timers, the
 *  focus cursor, the pin and the restore. It is NOT `startedAt`: the athlete
 *  may correct the start time mid-workout and nothing else should notice.
 *
 *  - a true edit (`log-past`) is the SAVED session's id (`editing.sessionId`),
 *    because the draft hydrated from it gets a fresh id on every open and
 *    reopening the same correction must give the same key;
 *  - everything else is the draft's own id. A REPEAT has a non-null `editing`
 *    whose `sessionId` is the SOURCE session, so it must NOT use it: it is a
 *    new session and needs a key different from the source's;
 *  - `startedAt` is the last resort for a draft with no id (the type allows
 *    it; hydrateDraft fills one on every draft, so this should not happen). */
export const sessionKeyOf = (
  draft: { sessionId?: string; startedAt: string },
  editing: { sessionId: string } | null,
  mode: LoggerMode,
): string =>
  mode === 'log-past' && editing ? editing.sessionId : (draft.sessionId ?? draft.startedAt);
